<template>
  <div ref="popup" class="sp-popup" part="base" :class="modifiersClass" :style="popupStyle" :popover="popoverMode">
    <div ref="pointer" class="sp-popup__pointer" :style="arrowStyle"></div>
    <div class="sp-popup__content">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, useTemplateRef, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { toBoolean } from "#ce/utils/props";
import { props as popupProps } from "./props.js";
import { usePopupPlacement } from "./composables/usePopupPlacement.js";
import { usePopupVisibility } from "./composables/usePopupVisibility.js";

const props = defineProps(popupProps);
const emit = defineEmits(["close"]);

const popup = useTemplateRef("popup");
const pointer = useTemplateRef("pointer");

const isActive = ref(toBoolean(props.active));
watch(
  () => props.active,
  (value) => (isActive.value = toBoolean(value)),
);

const isPermanent = computed(() => toBoolean(props.permanent));
const hasPointer = computed(() => toBoolean(props.pointer));

// `permanent` renders inline (no top layer); everything else floats in the top
// layer via the Popover API so it escapes ancestor clipping/overflow/stacking
// and nests correctly (a select inside a menu, etc.) — unless the host opts out
// via `--sp-comp-popup-use-top-layer` (see usePopupVisibility).
const isFloating = computed(() => !isPermanent.value);

const { popupStyle, arrowStyle, isLocationTop, startAutoUpdate, stopAutoUpdate } = usePopupPlacement({
  popup,
  arrowEl: pointer,
  anchor: () => props.anchor,
  location: () => props.location,
  horizontalAlign: () => props.horizontalAlign,
  hasPointer,
  enabled: isFloating,
  isActive,
});

const { popoverMode, useFallbackOpen } = usePopupVisibility({
  popup,
  isActive,
  enabled: isFloating,
  startAutoUpdate,
  stopAutoUpdate,
  noRestoreFocus: () => toBoolean(props.noRestoreFocus),
});

function handleClosed(active, previous) {
  if (!active && previous) {
    emit("close");
  }
}

// Visibility's watcher hides the native popover before this post-render notification.
watch(isActive, handleClosed, { flush: "post" });

const { breakpointModifiers } = useBreakpoints();

const modifiersClass = computed(() => {
  const modifiers = {
    ...breakpointModifiers.value,
    "--disabled": toBoolean(props.disabled),
    "--is-permanent": isPermanent.value,
    "--is-active": isActive.value,
    "--open": useFallbackOpen.value,
    "--pointer": hasPointer.value,
    [`--location-${props.location}`]: props.location,
    "--location-top": isLocationTop.value,
    [`--horizontal-align-${props.horizontalAlign}`]: props.horizontalAlign,
  };

  return modifiers;
});
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-popup {
  --popup-width-fallback-value: 18.5rem;
  --popup-min-width: var(--sp-comp-popup-min-width, var(--popup-width-fallback-value));
  --popup-width: var(--sp-comp-popup-width, var(--popup-min-width, var(--popup-width-fallback-value)));
  --popup-radius: var(--sp-comp-menu-border-radius, 0.5rem);
  --popup-bg-color: var(--sp-comp-popup-background-color, #fff);
  --display-pointer: none;
  --popup-pointer-size: var(--sp-comp-popup-pointer-size, 1rem);
  --popup-border-width: var(--sp-comp-popup-border-width, 0px); // (px) unit is in that case required for calc() reasons
  --popup-border-style: var(--sp-comp-popup-border-style, solid);
  --popup-border-color: var(--sp-comp-popup-border-color, transparent);
  --popup-border: var(--popup-border-width) var(--popup-border-style) var(--popup-border-color);
  --popup-box-shadow: var(--sp-comp-popup-box-shadow, 0 2px 3px rgba(0, 0, 0, 0.3));

  // Floating popover: shown in the top layer, positioned by Floating UI (inline
  // styles). Reset the UA popover defaults so our styles win.
  display: none;
  margin: 0;
  padding: 0;
  inset: auto;
  overflow: visible;
  color: inherit;
  background-color: var(--popup-bg-color);
  box-shadow: var(--popup-box-shadow);
  min-width: var(--popup-min-width);
  max-width: var(--sp-comp-popup-max-width, 60ch);
  min-height: var(--sp-comp-popup-min-height, 0);
  width: var(--popup-width);
  border: var(--popup-border);
  border-radius: var(--popup-radius);
  z-index: var(--sp-comp-popup-z-index, 10);
  box-sizing: border-box;

  &:popover-open {
    display: block;
  }

  // Fallback for browsers without the Popover API — and hosts opted out of the
  // top layer: not in the top layer, but Floating UI still positions it.
  // Mirrors the `:popover-open` reveal.
  &.--open {
    display: block;
  }

  &.--is-permanent {
    --popup-width: 100%;
    --display-pointer: none;
    position: relative;
    box-shadow: none;
    display: block;
    height: 100%;
    min-height: 0;
  }

  &.--pointer {
    --display-pointer: block;
  }
}

.sp-popup__pointer {
  --pointer-size: var(--popup-pointer-size);

  position: absolute;
  top: 0;
  width: var(--pointer-size);
  height: var(--pointer-size);
  display: var(--display-pointer);
  z-index: 1;

  /* Flush against the edge it points from, the same as `top: 0` is on the other side: the half-size
     offset that makes the diamond straddle that edge belongs to `::before` alone. */
  .--location-top & {
    top: unset;
    bottom: 0;

    &::before {
      top: unset;
      bottom: calc(var(--pointer-size) / 2 * -1);
    }
  }

  &::before {
    position: absolute;
    left: 0;
    content: "";
    transform: rotate(-45deg);
    top: calc(var(--pointer-size) / 2 * -1);
    width: var(--pointer-size);
    height: var(--pointer-size);
    box-shadow: var(--popup-box-shadow);
    z-index: -1;
    border: var(--popup-border);
    background-color: var(--popup-bg-color);
  }
}

.sp-popup__content {
  background-color: var(--popup-bg-color);
  border-radius: var(--sp-comp-popup-content-border-radius, var(--popup-radius));
  position: relative;
  padding: var(--sp-comp-popup-content-padding, 0);
  z-index: 2;
  max-height: var(--sp-popup-available-height, none);
  overflow: var(--sp-comp-popup-content-overflow, auto);

  .--is-permanent & {
    height: 100%;
    max-height: none;
  }
}
</style>
